import { EnumTheme, useAuthProvider } from "@components/AuthProvider";
import { RowMenu } from "@components/RowMenu";
import { RowMenuItem } from "@components/RowMenuItem";
import useLoading from "@hooks/useLoading";
import { useMenu } from "@hooks/useMenu";
import useMessage from "@hooks/useMessage";
import { Box, makeStyles } from "@material-ui/core";
import IconButton from "@material-ui/core/IconButton";
import Toolbar from "@material-ui/core/Toolbar";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import ColorLensIcon from '@material-ui/icons/ColorLens';
import InvertColorsIcon from '@material-ui/icons/InvertColors';
import MenuIcon from '@material-ui/icons/Menu';
import PowerSettingsNewIcon from '@material-ui/icons/PowerSettingsNew';
import { useAuthService } from "@services/AuthService";
import clsx from 'clsx';
import React, { MouseEventHandler } from "react";
import { useHistory } from "react-router-dom";

type AppToolbarProps = {
    onMenuButtonClick: MouseEventHandler<HTMLButtonElement>
    isMenuOpened: boolean
}

export default function AppToolbar({ onMenuButtonClick, isMenuOpened }: AppToolbarProps) {
    const classes = useStyles();

    const authService = useAuthService();
    const loading = useLoading();
    const message = useMessage();
    const authProvider = useAuthProvider();
    const history = useHistory();
    const menu = useMenu();

    const handleLogout = React.useCallback(() => {
        loading.start();
        authService.logout()
            .then(() => {
                authProvider?.clearAuth();
                history.push("login");
            })
            .catch(message.showException)
            .finally(loading.stop)
    }, [authProvider, authService, history, loading, message.showException]);

    const handleChangeTheme = React.useCallback((themeName: EnumTheme) => () => {
        if (authProvider !== undefined) {
            authProvider?.setState({
                ...authProvider.state,
                themeName
            })
        }
    }, [authProvider]);

    return (
        <Toolbar>
            <IconButton
                color="inherit"
                aria-label="abrir menu"
                onClick={onMenuButtonClick}
                edge="start"
                className={clsx(classes.menuButton, isMenuOpened && classes.hide)}
            >
                <MenuIcon />
            </IconButton>

            <Typography variant="h6" noWrap>
                MOBI 
            </Typography>

            <Box marginX={1} border={1} borderRadius={3} paddingY={.2} paddingX={.5}>
                <Typography variant="caption" noWrap>
                    07.10.2025
                </Typography>
            </Box>

            <div className={classes.right}>
                <span className={classes.name}>Olá, {authProvider?.state?.user?.displayName.split(' ')[0] || ""}</span>

                <Tooltip title="Alterar Tema">
                    <IconButton color="inherit" onClick={menu.handleOpen}>
                        <ColorLensIcon />
                    </IconButton>
                </Tooltip>

                <Tooltip title="Sair" onClick={handleLogout}>
                    <IconButton color="inherit">
                        <PowerSettingsNewIcon />
                    </IconButton>
                </Tooltip>
            </div>

            <RowMenu 
                menu={menu}
            >
                <RowMenuItem 
                    key={0} 
                    onClick={menu.handleItemClick(handleChangeTheme(EnumTheme.Default))}
                    icon={<InvertColorsIcon style={{color: '0e328c'}} />}
                    text="Padrão (Azul)"
                />

                <RowMenuItem 
                    key={1} 
                    onClick={menu.handleItemClick(handleChangeTheme(EnumTheme.Pink))}
                    icon={<InvertColorsIcon style={{color: '#B23FB5'}} />}
                    text="Rosa"
                />

                <RowMenuItem 
                    key={1} 
                    onClick={menu.handleItemClick(handleChangeTheme(EnumTheme.Purple))}
                    icon={<InvertColorsIcon style={{color: '#5F3FB5'}} />}
                    text="Lilás"
                />

            </RowMenu>
        </Toolbar>
    )
}

const useStyles = makeStyles(theme => ({
    right: {
        marginLeft: 'auto',
    },

    menuButton: {
        marginRight: theme.spacing(2),
    },

    name: {
        marginRight: 24
    },
    
    hide: {
        display: 'none',
    },    
}));
